import { Avatar, Group, MultiSelect, Text } from '@mantine/core';
import { useEffect } from 'react';
import { useOwners } from '@/models/owners';

type Props = {
  value: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  w?: number | string;
  size?: string;
};

/**
 * Owner filter shared by the Leads, Contacts, Accounts and Offers boards.
 *
 * Multi-select because the whole point is "mine, or mine and a colleague's".
 * An empty selection means no owner filter at all, not "nobody".
 */
export function OwnerFilter({
  value,
  onChange,
  placeholder = 'Owner',
  w = 240,
  size = 'xs',
}: Props) {
  const { data, isLoading } = useOwners();
  const owners = data?.owners ?? [];

  useEffect(() => {
    if (!data || !value.length) return;
    const known = new Set(data.owners.map((o) => String(o.id)));
    const kept = value.filter((id) => known.has(String(id)));
    if (kept.length !== value.length) onChange(kept);
  }, [data, value, onChange]);

  return (
    <MultiSelect
      size={size}
      w={w}
      placeholder={value.length ? undefined : placeholder}
      searchable
      clearable
      disabled={isLoading}
      data={owners.map((o) => ({ value: String(o.id), label: o.name }))}
      value={value.map(String)}
      onChange={(v) => onChange(v.map(Number))}
      renderOption={({ option }) => {
        const owner = owners.find((o) => String(o.id) === option.value);
        return (
          <Group gap={8} wrap="nowrap">
            <Avatar src={owner?.picture} size={20} radius="xl">
              {option.label.slice(0, 2).toUpperCase()}
            </Avatar>
            <Text fz="xs">{option.label}</Text>
          </Group>
        );
      }}
    />
  );
}
